import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Edit, Delete, Refresh, Search, Add } from '@mui/icons-material';
import hardwareService, { HardwareDevice, CreateHardwareDeviceRequest } from '../../services/hardwareService';
import { getAllProjects, Project } from '../../services/projectService';

const HardwareTab: React.FC = () => {
  const [devices, setDevices] = React.useState<HardwareDevice[]>([]);
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingDevice, setEditingDevice] = React.useState<HardwareDevice | null>(null);
  const [scanDialogOpen, setScanDialogOpen] = React.useState(false);
  const [scanning, setScanning] = React.useState(false);
  const [scanResults, setScanResults] = React.useState<any[]>([]);
  
  const [formData, setFormData] = React.useState<CreateHardwareDeviceRequest>({
    device_name: '',
    device_ip: '',
    tailscale_ip: '',
    project_id: '',
    camera_count: 0,
  });
  
  const [filterProject, setFilterProject] = React.useState<string>('');
  const [filterStatus, setFilterStatus] = React.useState<string>('');

  const loadDevices = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await hardwareService.list(filterProject || undefined, filterStatus || undefined);
      setDevices(data);
    } catch (e: any) {
      setError(e?.message || 'Nie udało się pobrać urządzeń');
    } finally {
      setLoading(false);
    }
  }, [filterProject, filterStatus]);

  const loadProjects = React.useCallback(async () => {
    try {
      const data = await getAllProjects();
      setProjects(data);
    } catch (e: any) {
      console.error('Błąd pobierania projektów:', e);
    }
  }, []);

  React.useEffect(() => {
    loadDevices();
    loadProjects();
  }, [loadDevices, loadProjects]);

  const handleOpenDialog = (device?: HardwareDevice) => {
    if (device) {
      setEditingDevice(device);
      setFormData({
        device_name: device.device_name,
        device_ip: device.device_ip,
        tailscale_ip: device.tailscale_ip || '',
        project_id: device.project_id || '',
        camera_count: device.camera_count,
      });
    } else {
      setEditingDevice(null);
      setFormData({
        device_name: '',
        device_ip: '',
        tailscale_ip: '',
        project_id: '',
        camera_count: 0,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDevice(null);
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      if (editingDevice) {
        await hardwareService.update(editingDevice.id, formData);
        setSuccess('Urządzenie zostało zaktualizowane');
      } else {
        await hardwareService.create(formData);
        setSuccess('Urządzenie zostało dodane');
      }
      handleCloseDialog();
      loadDevices();
    } catch (e: any) {
      setError(e?.message || 'Nie udało się zapisać urządzenia');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to urządzenie?')) return;
    setLoading(true);
    setError(null);
    try {
      await hardwareService.delete(id);
      setSuccess('Urządzenie zostało usunięte');
      loadDevices();
    } catch (e: any) {
      setError(e?.message || 'Nie udało się usunąć urządzenia');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckStatus = async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      await hardwareService.checkStatus(id);
      setSuccess('Status został sprawdzony');
      loadDevices();
    } catch (e: any) {
      setError(e?.message || 'Nie udało się sprawdzić statusu');
    } finally {
      setLoading(false);
    }
  };

  const handleScanNetwork = async () => {
    setScanDialogOpen(true);
    setScanResults([]);
  };

  const handleScanExecute = async (useTailscale: boolean, ipRange?: string) => {
    setScanning(true);
    setError(null);
    try {
      const result = await hardwareService.scanNetwork({
        use_tailscale: useTailscale,
        ip_range: ipRange,
      });
      setScanResults(result.data || []);
    } catch (e: any) {
      setError(e?.message || 'Nie udało się przeskanować sieci');
    } finally {
      setScanning(false);
    }
  };

  const handleAddFromScan = async (scanResult: any) => {
      setFormData({
        device_name: scanResult.device_name,
        device_ip: scanResult.device_ip,
        tailscale_ip: scanResult.tailscale_ip || '',
        project_id: '',
        camera_count: 0,
        device_info: scanResult.device_info,
      });
    setScanDialogOpen(false);
    setDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'success';
      case 'offline': return 'error';
      case 'scanning': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'online': return 'Online';
      case 'offline': return 'Offline';
      case 'scanning': return 'Skanowanie';
      default: return 'Nieznany';
    }
  };

  return (
    <Box sx={{ px: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Urządzenia Hardware (Raspberry Pi)
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<Search />}
            onClick={handleScanNetwork}
          >
            Skanuj sieć
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Dodaj urządzenie
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filtruj po projekcie</InputLabel>
            <Select
              value={filterProject}
              label="Filtruj po projekcie"
              onChange={(e) => setFilterProject(e.target.value)}
            >
              <MenuItem value="">Wszystkie</MenuItem>
              {projects.map((p) => (
                <MenuItem key={p.project_id} value={p.project_id}>
                  {p.project_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filtruj po statusie</InputLabel>
            <Select
              value={filterStatus}
              label="Filtruj po statusie"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="">Wszystkie</MenuItem>
              <MenuItem value="online">Online</MenuItem>
              <MenuItem value="offline">Offline</MenuItem>
              <MenuItem value="unknown">Nieznany</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadDevices}
            disabled={loading}
          >
            Odśwież
          </Button>
        </Stack>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nazwa urządzenia</TableCell>
                <TableCell>Adres IP</TableCell>
                <TableCell>Tailscale IP</TableCell>
                <TableCell>Projekt</TableCell>
                <TableCell>Liczba kamer</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ostatnio widziane</TableCell>
                <TableCell align="right">Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && devices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : devices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Brak urządzeń
                  </TableCell>
                </TableRow>
              ) : (
                devices.map((device) => (
                  <TableRow key={device.id}>
                    <TableCell>{device.device_name}</TableCell>
                    <TableCell>{device.device_ip}</TableCell>
                    <TableCell>{device.tailscale_ip || '—'}</TableCell>
                    <TableCell>
                      {device.project ? device.project.project_name : '—'}
                    </TableCell>
                    <TableCell>{device.camera_count}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(device.status)}
                        color={getStatusColor(device.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {device.last_seen_at
                        ? new Date(device.last_seen_at).toLocaleString('pl-PL')
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleCheckStatus(device.id)}
                        disabled={loading}
                        title="Sprawdź status"
                      >
                        <Refresh fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(device)}
                        title="Edytuj"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(device.id)}
                        disabled={loading}
                        title="Usuń"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog dodawania/edycji urządzenia */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDevice ? 'Edytuj urządzenie' : 'Dodaj urządzenie'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nazwa urządzenia"
              value={formData.device_name}
              onChange={(e) => setFormData({ ...formData, device_name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Adres IP (LAN)"
              value={formData.device_ip}
              onChange={(e) => setFormData({ ...formData, device_ip: e.target.value })}
              fullWidth
              required
              helperText="Lokalna sieć (np. 192.168.x). Do podglądu z VPS użyj też Tailscale poniżej."
            />
            <TextField
              label="Tailscale IP lub MagicDNS (zalecane z produkcji)"
              value={formData.tailscale_ip}
              onChange={(e) => setFormData({ ...formData, tailscale_ip: e.target.value })}
              fullWidth
              helperText="Jeśli wypełnione — proxy HLS i status używają tego adresu pierwszego (nie LAN)."
            />
            <FormControl fullWidth>
              <InputLabel>Projekt</InputLabel>
              <Select
                value={formData.project_id || ''}
                label="Projekt"
                onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
              >
                <MenuItem value="">Brak przypisania</MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p.project_id} value={p.project_id}>
                    {p.project_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Liczba kamer"
              type="number"
              value={formData.camera_count}
              onChange={(e) => setFormData({ ...formData, camera_count: parseInt(e.target.value) || 0 })}
              fullWidth
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              Status urządzenia będzie automatycznie wykryty podczas zapisywania (sprawdzenie przez ping).
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Anuluj</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog skanowania sieci */}
      <Dialog open={scanDialogOpen} onClose={() => setScanDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Skanuj sieć w poszukiwaniu urządzeń</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Wybierz metodę skanowania: Tailscale (automatyczne wykrywanie urządzeń w sieci Tailscale) lub zakres IP (pingowanie adresów).
            </Alert>
            <Button
              variant="outlined"
              onClick={() => handleScanExecute(true)}
              disabled={scanning}
              fullWidth
            >
              Skanuj przez Tailscale
            </Button>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Zakres IP (np. 192.168.1.0/24 lub 192.168.1.1-192.168.1.254)"
                value={formData.device_ip}
                onChange={(e) => setFormData({ ...formData, device_ip: e.target.value })}
                fullWidth
                placeholder="192.168.1.0/24"
              />
              <Button
                variant="outlined"
                onClick={() => handleScanExecute(false, formData.device_ip)}
                disabled={scanning || !formData.device_ip}
              >
                Skanuj IP
              </Button>
            </Box>
            {scanning && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
              </Box>
            )}
            {scanResults.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Znalezione urządzenia ({scanResults.length}):
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nazwa</TableCell>
                        <TableCell>IP</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Akcja</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scanResults.map((result, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{result.device_name}</TableCell>
                          <TableCell>{result.device_ip}</TableCell>
                          <TableCell>
                            <Chip label={result.status} size="small" color="success" />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              onClick={() => handleAddFromScan(result)}
                            >
                              Dodaj
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScanDialogOpen(false)}>Zamknij</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HardwareTab;
